import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';
import { api, ApiError } from '../api';
import { formatPln } from '../catalog/money';
import { dayLabel, ibanLabel } from './format';
import { BANK_KEY, type Transaction } from './types';

type Direction = '' | 'in' | 'out';
const DIRECTIONS: Array<[Direction, string]> = [
  ['', 'Wszystkie'],
  ['in', 'Wpływy'],
  ['out', 'Wydatki'],
];

/** Signed amount: "+1 230,00 zł" for money in, "−250,50 zł" for money out. */
const signedPln = (g: number) => `${g > 0 ? '+' : g < 0 ? '−' : ''}${formatPln(Math.abs(g))}`;

/** "Operacje" of the statements (F37): filters by days, direction and text; newest first. */
export function TransactionsTable() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [direction, setDirection] = useState<Direction>('');
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (direction) params.set('direction', direction);
  if (query) params.set('q', query);
  const list = useQuery({
    queryKey: [...BANK_KEY, 'transactions', from, to, direction, query],
    queryFn: () => api<{ transactions: Transaction[]; truncated: boolean }>(`/api/bank/transactions${params.size > 0 ? `?${params}` : ''}`),
  });
  const rows = list.data?.transactions ?? [];
  // a role without the amounts' right gets no `amountGrosze` at all: no column then
  const showAmount = rows.length === 0 || rows.some((r) => r.amountGrosze !== undefined);

  return (
    <section aria-label="Operacje" className="bank-transactions" data-feature-id="bank.statements">
      <h2>Operacje</h2>
      <div className="bank-filters">
        <label className="field">
          <span>Od</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Do</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {/* the direction is the sign of the amount: offered only to whoever sees amounts (the server ignores it otherwise) */}
        {showAmount && (
          <div className="segment" role="group" aria-label="Kierunek">
            {DIRECTIONS.map(([value, label]) => (
              <button key={label} type="button" aria-pressed={direction === value} onClick={() => setDirection(value)}>
                {label}
              </button>
            ))}
          </div>
        )}
        <label className="field bank-search">
          <span>Szukaj</span>
          <input type="search" value={q} placeholder="Nazwa, tytuł, rachunek" onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać operacji'}
        </p>
      )}
      {list.data && rows.length === 0 && <p className="muted">Brak operacji{from || to || direction || query ? ' dla wybranych filtrów' : ' — wgraj wyciąg'}.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table bank-table" aria-label="Operacje z wyciągów">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Nadawca / odbiorca</th>
                <th scope="col" className="wide-only">
                  Tytuł
                </th>
                {showAmount && (
                  <th scope="col" className="num">
                    Kwota
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td className="nowrap">{dayLabel(t.bookingDate)}</td>
                  <td>
                    {t.counterpartyName || <span className="muted">—</span>}
                    {t.counterpartyIban && <div className="muted small">{ibanLabel(t.counterpartyIban)}</div>}
                    <div className="phone-only bank-title">{t.title}</div>
                  </td>
                  <td className="bank-title wide-only">{t.title}</td>
                  {showAmount && <td className="num nowrap">{t.amountGrosze === undefined ? '—' : signedPln(t.amountGrosze)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data?.truncated && <p className="muted">Pokazano 500 najnowszych operacji — zawęź okres albo wyszukiwanie.</p>}
    </section>
  );
}
